import { LayerModel } from './LayerModel.js';
import { BoringModel } from './BoringModel.js';
import { MonsterModel } from './MonsterModel.js';
import type { ResultData, JsonLaag, JsonMeetpunt, JsonMonster, Boring, Monster } from '../types/index.js';

/**
 * Framework-agnostic project model.
 * Accepts either a ResultData object or a raw JSON string.
 */
export class ProjectModel {
  readonly isValid: boolean;
  readonly borings: BoringModel[];
  readonly allLayers: LayerModel[];
  readonly allMonsters: MonsterModel[];
  readonly projectName: string | null;
  readonly projectCode: string | null;

  constructor(data: ResultData | string | null | undefined, _projectId?: string | null, createdAt?: string | null) {
    let parsed: ResultData = {};
    try {
      parsed = typeof data === 'string' ? JSON.parse(data) : (data ?? {});
      // Handle double-parse: if still a string after first parse, parse again
      if (typeof parsed === 'string') parsed = JSON.parse(parsed as unknown as string);
      this.isValid = true;
    } catch {
      this.isValid = false;
    }

    this.projectName = parsed.Project?.name?.waarde ?? null;
    this.projectCode = parsed.Project?.projectCode?.waarde ?? null;

    const rawLagen: JsonLaag[] = Array.isArray(parsed.Lagen) ? parsed.Lagen : [];
    this.allLayers = rawLagen.map(l => new LayerModel(l));

    const rawMonsters: JsonMonster[] = Array.isArray(parsed.Monsters) ? parsed.Monsters : [];
    this.allMonsters = rawMonsters.map(m => new MonsterModel(m));

    const rawMeetpunten: JsonMeetpunt[] = Array.isArray(parsed.Meetpunten) ? parsed.Meetpunten : [];
    this.borings = rawMeetpunten.map(mp => new BoringModel(mp, this.allLayers, this.allMonsters, createdAt));

    // Fallback: if no meetpunten defined but layers exist, group under one boring
    if (this.borings.length === 0 && this.allLayers.length > 0) {
      this.borings = [new BoringModel({ name: { waarde: 'Boring 1' } }, this.allLayers, this.allMonsters, createdAt)];
    }
  }

  /** Alias for backwards-compat with backoffice code that uses .meetpunten */
  get meetpunten(): BoringModel[] {
    return this.borings;
  }

  /**
   * Construct a ProjectModel from a normalized flat API response.
   * @param data  Response from GET /api/projects/:id/borings (flat Boring[] with nested layers/monsters)
   */
  static fromFlat(data: {
    borings?: (Boring & { layers?: any[]; monsters?: any[] })[];
    monsters?: Monster[];
    projectName?: string | null;
    projectCode?: string | null;
  }): ProjectModel {
    const instance = Object.create(ProjectModel.prototype) as ProjectModel;
    (instance as any).isValid = true;
    (instance as any).projectName = data.projectName ?? null;
    (instance as any).projectCode = data.projectCode ?? null;
    (instance as any).borings = (data.borings ?? []).map(b => BoringModel.fromFlat(b));
    (instance as any).allLayers = (instance as any).borings.flatMap((b: BoringModel) => b.layers);
    (instance as any).allMonsters = (instance as any).borings.flatMap((b: BoringModel) => b.monsters);
    return instance;
  }
}
